import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Grid,
  Card,
  CardMedia,
  CircularProgress,
  Checkbox,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Photo as PhotoIcon,
  Videocam as VideoIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { ImageViewer, type MediaItem } from '../media/ImageViewer';
import { getMediaFiles } from '../../services/api/familypal';
import { uploadMediaFileWeb } from '../../services/api/familypal/mediaUpload';
import { updateData } from '../../services/dataApi';
import { colors } from '../../theme';

interface AlbumGalleryDialogProps {
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete?: () => void;
  album: {
    id: string;
    data?: {
      data?: {
        name?: string;
        fileIds?: Array<string | { id: string; type: string }>;
      };
    };
  } | null;
}

interface DisplayMedia extends MediaItem {
  thumbnailUri: string;
}

// Normalize file entries from albums (handles both string IDs and typed entries)
const normalizeFileEntry = (entry: any): { id: string; type: 'image' | 'video' } => {
  if (typeof entry === 'string') {
    return { id: entry, type: 'image' };
  }
  return {
    id: entry.id || entry,
    type: entry.type === 'video' ? 'video' : 'image',
  };
};

export const AlbumGalleryDialog: React.FC<AlbumGalleryDialogProps> = ({
  open,
  onClose,
  onEdit,
  onDelete,
  album,
}) => {
  const [media, setMedia] = useState<DisplayMedia[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const albumData = album?.data?.data;
  const albumName = albumData?.name || 'Untitled Album';
  const fileEntries = albumData?.fileIds || [];
  const albumId = album?.id;

  // Fetch media files when album changes
  useEffect(() => {
    if (!open || !albumId || fileEntries.length === 0) {
      setMedia([]);
      return;
    }

    const fetchMedia = async () => {
      setLoading(true);
      setError(null);

      try {
        const normalizedEntries = fileEntries.map(normalizeFileEntry);
        const fileIds = normalizedEntries.map((e) => e.id);

        if (fileIds.length === 0) {
          setMedia([]);
          return;
        }

        // Create a type map for lookup
        const typeMap = new Map(normalizedEntries.map((e) => [e.id, e.type]));

        // Fetch media file metadata
        const result = await getMediaFiles(fileIds);
        const files = (result as any)?.files || [];

        // Transform to display format
        const displayMedia: DisplayMedia[] = files.map((file: any) => ({
          id: file.id,
          uri: file.uri || file.url,
          thumbnailUri: file.thumbnailUri || file.thumbnail_url || file.uri || file.url,
          type: typeMap.get(file.id) || 'image',
          miniPreview: file.miniPreview || file.mini_preview,
        }));

        setMedia(displayMedia);
      } catch (err: any) {
        console.error('Failed to fetch album media:', err);
        const message = err?.response?.data?.message || err?.message || 'Failed to load album contents';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchMedia();
  }, [open, albumId]);

  // Reset selection when closing
  useEffect(() => {
    if (!open) {
      setSelectionMode(false);
      setSelectedIds(new Set());
    }
  }, [open]);

  const handleMediaClick = (index: number) => {
    if (selectionMode) {
      const item = media[index];
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(item.id)) {
          next.delete(item.id);
        } else {
          next.add(item.id);
        }
        return next;
      });
    } else {
      setViewerIndex(index);
      setViewerOpen(true);
    }
  };

  const handleLongPress = (index: number) => {
    setSelectionMode(true);
    setSelectedIds(new Set([media[index].id]));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !albumId) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const newFileEntries: Array<{ id: string; type: string }> = [];
      const total = files.length;

      for (let i = 0; i < total; i++) {
        const file = files[i];
        const isVideo = file.type.startsWith('video/');
        const fileForUpload = {
          uri: URL.createObjectURL(file),
          name: file.name,
          mimeType: file.type || 'application/octet-stream',
          size: file.size,
        };

        const uploaded = await uploadMediaFileWeb(fileForUpload, albumId, () => {});
        URL.revokeObjectURL(fileForUpload.uri);

        newFileEntries.push({ id: uploaded.id, type: isVideo ? 'video' : 'image' });
        setUploadProgress(Math.round(((i + 1) / total) * 100));
      }

      // Update album data with new file IDs
      const existingEntries = fileEntries.map(normalizeFileEntry).map((e) => ({ id: e.id, type: e.type }));
      const allEntries = [...existingEntries, ...newFileEntries];
      const albumUpdateData = { ...albumData, fileIds: allEntries };
      await updateData(albumId, 'album', albumUpdateData);

      // Refresh media list
      const result = await getMediaFiles(allEntries.map((e) => e.id));
      const fetchedFiles = (result as any)?.files || [];
      const typeMap = new Map(allEntries.map((e) => [e.id, e.type]));
      setMedia(fetchedFiles.map((f: any) => ({
        id: f.id,
        uri: f.uri || f.url,
        thumbnailUri: f.thumbnailUri || f.thumbnail_url || f.uri || f.url,
        type: typeMap.get(f.id) || 'image',
        miniPreview: f.miniPreview || f.mini_preview,
      })));
    } catch (err) {
      console.error('Failed to upload files:', err);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const photoCount = media.filter((m) => m.type === 'image').length;
  const videoCount = media.filter((m) => m.type === 'video').length;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6">{albumName}</Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                {photoCount > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhotoIcon fontSize="inherit" /> {photoCount} photos
                  </Typography>
                )}
                {videoCount > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <VideoIcon fontSize="inherit" /> {videoCount} videos
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                Add Photos
              </Button>
              <IconButton onClick={onEdit} size="small" title="Edit album">
                <EditIcon />
              </IconButton>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ minHeight: 400 }}>
          {uploading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">Uploading... {uploadProgress}%</Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : media.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: 300,
                color: 'text.secondary',
              }}
            >
              <PhotoIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
              <Typography>No photos or videos in this album</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Edit the album to add media
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={1}>
              {media.map((item, index) => (
                <Grid size={{ xs: 4, sm: 3, md: 2 }} key={item.id}>
                  <Card
                    sx={{
                      position: 'relative',
                      aspectRatio: '1',
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.9 },
                      ...(selectionMode && selectedIds.has(item.id) && {
                        outline: `3px solid ${colors.memories}`,
                      }),
                    }}
                    onClick={() => handleMediaClick(index)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      handleLongPress(index);
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={item.thumbnailUri || item.uri}
                      alt=""
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        backgroundImage: item.miniPreview ? `url(data:image/jpeg;base64,${item.miniPreview})` : undefined,
                        backgroundSize: 'cover',
                      }}
                    />
                    {item.type === 'video' && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          bgcolor: 'rgba(0,0,0,0.5)',
                          borderRadius: '50%',
                          p: 0.5,
                        }}
                      >
                        <PlayIcon sx={{ color: 'white' }} />
                      </Box>
                    )}
                    {selectionMode && (
                      <Checkbox
                        checked={selectedIds.has(item.id)}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'rgba(255,255,255,0.8)',
                          borderRadius: 1,
                          p: 0,
                        }}
                      />
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          {selectionMode ? (
            <>
              <Typography variant="body2" sx={{ mr: 'auto' }}>
                {selectedIds.size} selected
              </Typography>
              <Button onClick={() => { setSelectionMode(false); setSelectedIds(new Set()); }}>
                Cancel
              </Button>
              <Button
                color="error"
                startIcon={<DeleteIcon />}
                disabled={selectedIds.size === 0}
              >
                Remove from Album
              </Button>
            </>
          ) : (
            <>
              {onDelete && (
                <Button onClick={onDelete} color="error" startIcon={<DeleteIcon />} sx={{ mr: 'auto' }}>
                  Delete Album
                </Button>
              )}
              <Button onClick={onClose}>Close</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Image/Video Viewer */}
      <ImageViewer
        items={media}
        initialIndex={viewerIndex}
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
      />
    </>
  );
};

export default AlbumGalleryDialog;
